// Localização: ./src/components/Common/ErrorMessage.jsx
// Componente melhorado para exibição de mensagens de erro

import React from 'react';
import { AlertCircle, WifiOff, ServerCrash, ShieldAlert, X } from 'lucide-react';

/**
 * Tipos de erro suportados
 */
export const ERROR_TYPES = {
    VALIDATION: 'validation',
    NETWORK: 'network',
    SERVER: 'server',
    PERMISSION: 'permission',
    GENERIC: 'generic'
};

/**
 * Componente de mensagem de erro melhorado
 * @param {Object} props
 * @param {string} props.message - Mensagem de erro
 * @param {string} props.type - Tipo de erro (validation, network, server, permission, generic)
 * @param {Function} props.onClose - Função para fechar a mensagem (opcional)
 * @param {Array<string>} props.details - Array de detalhes adicionais (opcional)
 */
const ErrorMessage = ({ message, type = ERROR_TYPES.GENERIC, onClose, details = [] }) => {
    const getErrorConfig = () => {
        const configs = {
            [ERROR_TYPES.VALIDATION]: {
                bgColor: 'bg-yellow-50',
                borderColor: 'border-yellow-300',
                textColor: 'text-yellow-800',
                icon: AlertCircle,
                iconColor: 'text-yellow-600',
                title: 'Erro de Validação'
            },
            [ERROR_TYPES.NETWORK]: {
                bgColor: 'bg-orange-50',
                borderColor: 'border-orange-300',
                textColor: 'text-orange-800',
                icon: WifiOff,
                iconColor: 'text-orange-600',
                title: 'Erro de Conexão'
            },
            [ERROR_TYPES.SERVER]: {
                bgColor: 'bg-red-50',
                borderColor: 'border-red-300',
                textColor: 'text-red-800',
                icon: ServerCrash,
                iconColor: 'text-red-600',
                title: 'Erro do Servidor'
            },
            [ERROR_TYPES.PERMISSION]: {
                bgColor: 'bg-purple-50',
                borderColor: 'border-purple-300',
                textColor: 'text-purple-800',
                icon: ShieldAlert,
                iconColor: 'text-purple-600',
                title: 'Erro de Permissão'
            },
            [ERROR_TYPES.GENERIC]: {
                bgColor: 'bg-red-50',
                borderColor: 'border-red-300',
                textColor: 'text-red-800',
                icon: AlertCircle,
                iconColor: 'text-red-600',
                title: 'Erro'
            }
        };
        
        return configs[type] || configs[ERROR_TYPES.GENERIC];
    };

    const config = getErrorConfig();
    const Icon = config.icon;

    return (
        <div 
            className={`${config.bgColor} ${config.borderColor} ${config.textColor} border-2 rounded-xl p-4 shadow-md relative`}
            role="alert"
        >
            <div className="flex items-start gap-3">
                <Icon className={`${config.iconColor} w-5 h-5 mt-0.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                            <h4 className="font-semibold text-sm mb-1">{config.title}</h4>
                            <p className="text-sm leading-relaxed">{message}</p>
                            {details && details.length > 0 && (
                                <ul className="mt-2 ml-4 list-disc space-y-1">
                                    {details.map((detail, index) => (
                                        <li key={index} className="text-sm">{detail}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className={`${config.textColor} hover:opacity-70 transition-opacity flex-shrink-0 p-1`}
                                aria-label="Fechar mensagem de erro"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorMessage;
